import { motion } from "framer-motion";
import { Check, UserCheck, TrendingUp, GraduationCap, Clock, Star } from "lucide-react";

/* ForWhoSection — Público alvo com emojis e bloco de reforço */
const profiles = [
  { emoji: "🏥", title: "Enfermeiros de plantão oncológico", desc: "Que precisam de respostas rápidas durante a assistência ao paciente." },
  { emoji: "📚", title: "Estudantes de residência multiprofissional", desc: "Que querem aprofundar conhecimento clínico prático em oncologia." },
  { emoji: "🎓", title: "Recém-formados na área oncológica", desc: "Que buscam segurança técnica desde o início da carreira." },
  { emoji: "⬆️", title: "Profissionais que querem evoluir", desc: "Que desejam atualização constante baseada em evidências." },
  { emoji: "🌙", title: "Plantonistas noturnos", desc: "Que precisam de suporte clínico a qualquer hora, sem esperar." },
  { emoji: "📱", title: "Quem prefere aprender pelo celular", desc: "Que valorizam praticidade e acesso rápido na palma da mão." },
];

const notFor = [
  "Médicos ou profissionais de outras áreas",
  "Quem busca apenas teoria sem aplicação prática",
  "Profissionais que não atuam ou não pretendem atuar em oncologia",
];

export function ForWhoSection() {
  return (
    <section className="py-16 sm:py-24 bg-background">
      <div className="container max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 sm:mb-14"
        >
          <span className="text-accent text-xs font-semibold uppercase tracking-widest mb-3 block">
            Público ideal
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            Para quem é a{" "}
            <span className="text-accent">Jornada Oncológica?</span>
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            Esta plataforma foi construída especificamente para enfermeiros que vivem a oncologia na prática.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-10 sm:mb-14">
          {/* Coluna PARA */}
          <div>
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-accent" />
              </div>
              <h3 className="font-bold text-lg text-foreground">É para você se…</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {profiles.map((profile, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.07 }}
                  className="bg-card border border-border rounded-2xl p-4 hover:border-accent/30 transition-colors"
                >
                  <span className="text-2xl mb-2 block">{profile.emoji}</span>
                  <h4 className="font-semibold text-foreground text-sm mb-1">{profile.title}</h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">{profile.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Coluna NÃO PARA */}
          <div>
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-destructive" />
              </div>
              <h3 className="font-bold text-lg text-foreground">Não é para você se…</h3>
            </div>
            <div className="space-y-3 mb-6 sm:mb-8">
              {notFor.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3"
                >
                  <span className="text-destructive font-bold text-base shrink-0">✕</span>
                  <p className="text-muted-foreground text-sm">{item}</p>
                </motion.div>
              ))}
            </div>

            {/* Bloco de reforço */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-accent/10 border-2 border-accent/30 rounded-2xl p-5 sm:p-6"
            >
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-warning text-warning" />
                ))}
              </div>
              <p className="text-foreground text-sm font-semibold mb-2">
                "Se você é enfermeiro na oncologia — iniciante ou experiente — esta plataforma vai mudar sua rotina de plantão."
              </p>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-accent" />
                <span className="text-xs text-muted-foreground">
                  Artur Cunha — Criador da Plataforma
                </span>
              </div>
            </motion.div>

            {/* Mini feature list */}
            <div className="space-y-2 mt-4 sm:mt-5">
              {["Acesso imediato", "Conteúdo validado clinicamente", "Garantia de 7 dias"].map((feat, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-accent shrink-0" />
                  {feat}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
